import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { IMqttMessage, MqttConnectionState, MqttService } from "ngx-mqtt";
import { BehaviorSubject, Observable, of, Subscription, throwError } from "rxjs";
import { catchError, filter, map, take, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ClientSettings } from "src/model/class/client-settings";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { ComponentAggregation } from "src/model/class/component classes/component-aggregation";
// import { SupervisionComponent } from "src/components/general components/supervision/supervision.component";
import { SupervisionComponentAPI } from "src/model/class/component classes/supervision-component-api";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import {
  AggregateOrder,
  CustomInterfaceAggregateOrder,
} from "src/model/class/general classes/custom-interface-aggregate-order";
import { DataStorage } from "src/model/class/general classes/data-storage";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { Plant } from "src/model/class/general classes/plant";
import { Supervision } from "src/model/class/general classes/supervision";
import { Settings } from "src/model/class/settings";
import { BIGDeviceOperationMode } from "src/components/shared-components/enum/enumDevice";
import { EnvironmentCategory } from "src/components/shared-components/enum/enumEnvironment";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { PageSection } from "src/model/enum/enumPage";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { clientSettingsUpdateCustomStartingAccessEnvironmentcartegoryAction } from "src/state/actions/client-settings.actions";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { selectedPlant } from "src/state/selectors/plant.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { AddressService } from "./address.service";
import { EventService } from "./event.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { PlantService } from "./plant.service";
import { SystemRouterService } from "./system-router.service";
import { ThemeService } from "./theme.service";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";

@Injectable({
  providedIn: "root",
})
// tslint:disable: no-string-literal
export class SupervisionService
  extends DataStorage
  implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "Supervision.service";

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectSelectedPlantUserSub: Subscription = null;
  private _clientSetting: ClientSettings = null;
  //#endregion

  private _supervisionStore: GenericApiData = null;
  private _supervisionListSubject: BehaviorSubject<Array<Supervision>> =
    new BehaviorSubject<Array<Supervision>>(null);
  private _currentPlant: Plant = null;
  private _currentBuilding: Building = null;
  private _userSettings: Settings = null;

  /**
   * Flag property; if the service ha foud an error in the last API call needed for get the supervision-list from the server, it is set to TRUE;
   * otherwise, it is set to FALSE.
   */
  private _isErrorInGetSupervisionListAPIRequest: boolean = false;

  /**Flag property; used to signal if a API call querying the server for the complete supervsions list is already ongoing; if so, a second call is NOT executed. */
  private _isGettingSupervsionListOnGoing: boolean = false;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  /**Id of last visited Supervision; this allows the service to restricts its operations (where needed) only to target the currently/last displayed Supervision */
  private _lastDiplayedSupervsionID: number = -1;

  public _eventSubscriptions: Array<Subscription> = null;
  public needToInitialize = true;
  public readonly listSupervision = this._supervisionListSubject.asObservable();

  public areWeInSupervisionsList: boolean = false;

  get isErrorInGetSupervisionListAPIRequest(): boolean {
    return this._isErrorInGetSupervisionListAPIRequest;
  }

  constructor(
    private _storageService: LocalStorageService,
    private _http: HttpClient,
    private _eventService: EventService,
    private _addressService: AddressService,
    private _mqttService: MqttClientService,
    private _loggerService: LoggerService,
    private _mqttServiceNative: MqttService,
    private _store: Store,
    private _route: ActivatedRoute,
    private _plantService: PlantService,
    private _systemRouterService: SystemRouterService,
    private _themeService: ThemeService
  ) {
    super();

    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    /** Subscrive event from Event Service */
    this.subscribeEvents();

    this.subscribeMQTTAddresses();
    this.subscribePlant();
    this.subscribeSettings();
    this.subscribeBuilding();
  }

  public subscribeSettings() {
    this._store.select(serverSettings).subscribe((settings: Settings) => {
      this._userSettings = settings;
    });

    this._store.select(clientSettings).subscribe((settings: ClientSettings) => {
      this._clientSetting = settings;
    });
  }

  /**
   * Subscribe to current building to reload only the right supervisions
   */
  public subscribeBuilding() {
    this._store
      .select(selectedBuilding)
      .subscribe((currentBuilding: Building) => {
        this._currentBuilding = currentBuilding;
      });
  }

  /**
   * Delete supervision
   * @param idSupervision
   */
  public deleteSupervision(idSupervision: number) {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMINTERFACE +
        "/" +
        idSupervision
      )
      .pipe(
        catchError((error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            " ._http.delete -" +
            AppSettings.API_URL_CUSTOMINTERFACE +
            "with idSupervision:" +
            idSupervision,
            " deleteSupervision() ",
            "ERROR: " + error.message
          );

          return of(null);
        })
      )
      .toPromise();
  }

  /**
   * Update supervision
   */
  public updateSupervision(idSupervision: number, description: string) {
    return this._http
      .patch<any>(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMINTERFACE +
        "/" +
        idSupervision,
        {
          name: description,
          kind: 1,
        }
      )
      .pipe(
        catchError((error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            " ._http.patch -" +
            AppSettings.API_URL_CUSTOMINTERFACE +
            "with description:" +
            description +
            "and idSupervision:" +
            idSupervision +
            "",
            " updateSupervision() ",
            "ERROR: " + error.message
          );

          return of(null);
        })
      )
      .toPromise();
  }
  /*
   * Create supervision
   */
  public createSupervision(
    idEnvironment: string,
    description: string,
    id_smartComponent?: number
  ) {
    let body = {
      name: description,
      kind: 1,
      isVirtual: 1,
      idSmartComponent: id_smartComponent,
    };

    body["associatedRooms"] = [idEnvironment];

    return this._http
      .post<any>(
        environment.httpServerRoot + AppSettings.API_URL_CUSTOMINTERFACE,
        body
      )
      .pipe(
        catchError((error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            " ._http.post -" +
            AppSettings.API_URL_CUSTOMINTERFACE +
            "with description:" +
            description +
            "and environment:" +
            idEnvironment +
            "",
            " createSupervision() ",
            "ERROR: " + error.message
          );

          return of(null);
        })
      )
      .toPromise();
  }

  /**
   * Check first page to open based on current client settings and current plant first page to open
   */
  public handleClientSettingsDefaultPageToOpen(): void {
    const supervisionToOpen = this._currentPlant.defaultSupervision.split(",");

    const idSupervision: number = parseInt(supervisionToOpen[0]);
    const idEnvironment: number = parseInt(supervisionToOpen[1]);

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleClientSettingsDefaultPageToOpen",
      "Page to open: idSupervision: " +
      idSupervision +
      " , idEnvironment: " +
      idEnvironment
    );

    this.navigateToSpecificSupervision(idSupervision, idEnvironment);
  }

  /**
   * navigate to specific supervision
   *
   * @param idSupervision ID supervision
   * @param idEnvironment ID environment
   */
  private navigateToSpecificSupervision(
    idSupervision: number,
    idEnvironment: number
  ): void {
    if (this._clientSetting.supervisionListViewMode === true) {
      this.navigateToSpecificSupervisionList(idSupervision, idEnvironment);
    } else {
      this.navigateToSpecificSupervisionGraph(idSupervision, idEnvironment);
    }
  }

  /**
   * Navigate into specific supervision list mode based on idSupervision and idEnvironment
   *
   * @param idSupervision ID Supervision to open
   * @param idEnvironment ID Environment to open
   */
  private navigateToSpecificSupervisionList(
    idSupervision: number,
    idEnvironment: number
  ): void {
    this._systemRouterService.navigateToSupervisionList(
      idSupervision,
      idEnvironment
    );
  }

  /**
   * Navigate into specific supervision graph mode based on idSupervision and idEnvironment
   *
   * @param idSupervision ID Supervision to open
   * @param idEnvironment ID Environment to open
   */
  private navigateToSpecificSupervisionGraph(
    idSupervision: number,
    idEnvironment: number
  ): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "navigateToSpecificSupervisionGraph",
      "Page to open: idSupervision: " +
      idSupervision +
      " , idEnvironment: " +
      idEnvironment
    );

    this._systemRouterService.navigateToSupervisionGraph(
      idSupervision,
      idEnvironment
    );
  }

  /**
   *  Initialize public observable list trasmission: it should be call in the costructor of all component/page that you need.
   */
  async Init(idCurrentSupervision: number = -1) {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "Init",
      "needToInitialize: " +
      this.needToInitialize +
      " || idCurrrentSupervision: " +
      idCurrentSupervision
    );
    if (idCurrentSupervision !== -1) {
      this._lastDiplayedSupervsionID = idCurrentSupervision;
    }

    if (this.needToInitialize) {
      //In order to make sure to "clean" the supervisions' list when we re-initialise the service, we set both the public observable and the private store to null
      this._supervisionListSubject.next(null);
      this._supervisionStore = null;

      this.needToInitialize = false;

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "Init",
        "IN Init()"
      );

      await this.initializeSupervisionList();

      //ATTENTION: moved above
      //this.needToInitialize = false;
    }
  }

  /** Event destroy service */
  ngOnDestroy() {
    this.unsubscribeEvents();

    this.unsubscribeStoreSelect();

    this._supervisionListSubject.next(null);
    this._supervisionListSubject.complete();

    this.unsubscribeToMQTTAddresses();

    this.areWeInSupervisionsList = false;
    this._isGettingSupervsionListOnGoing = false;
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    /** Operation performed on logout */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._storageService.remove(AppSettings.KEY_SUPERVISIONS);

        await this.unsubscribeToMQTTAddresses();

        this._isGettingSupervsionListOnGoing = false;
        this._supervisionStore = null;
        this._supervisionListSubject.next(null);
        this.needToInitialize = true;
        this._lastDiplayedSupervsionID = -1;
        this.areWeInSupervisionsList = false;
      })
    );

    this._eventSubscriptions.push(
      this._mqttServiceNative.state.subscribe(
        (currentStatus: MqttConnectionState) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "mqttService",
            "state",
            "Status mqtt state connection from service: " + currentStatus
          );
        }
      )
    );

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.subscribe(async () => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnComeBackOnline",
          "REACT."
        );

        await this.initializeSupervisionList(); //todo, is 'await' right?
      })
    );

    /** GF: when the mqtt come back online I recheck the supervision status because some change can be done
     * while the mqtt is offline (change of addresses but also change of component)
     *  AB: you can find this just her, beacuse we decided to update addresses just in case of supervisions,
     * not scenarios or schedules for example
     */
    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnlineMqtt.subscribe(async () => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnComeBackOnlineMqtt",
          "REACT."
        );

        //DEBUG for Store functionality
        const resultReadStorage: GenericApiData = await this.getFromStorage();

        /*If the Supervisions-related feature in Store is empty, then it is NOT "OnComeBackOnlineMqtt" duty to initialise it.*/
        if (resultReadStorage !== null) {
          await this.initializeSupervisionList();
        }

        if (this._supervisionStore !== null && !!this._supervisionStore.data) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnComeBackOnlineMqtt : OnCustomInterfaceChangedMqtt",
            "ID last displayed supervision: " + this._lastDiplayedSupervsionID
          );

          const lastDisplayedSupervision: Supervision = this.getSupervisionByID(
            this._lastDiplayedSupervsionID
          );

          if (lastDisplayedSupervision) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_eventService",
              "OnComeBackOnlineMqtt",
              "Sending 'OnComeBackOnlineMqtt : OnCustomInterfaceChangedMqtt' signal to: " +
              lastDisplayedSupervision.id
            );

            this._eventService.OnCustomInterfaceChangedMqtt.next(
              lastDisplayedSupervision.id
            );
          }

          if (this.areWeInSupervisionsList) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_eventService",
              "OnComeBackOnlineMqtt",
              "We are currently in Supervisions.page, sending OnSupervisionListChangeMqtt signal"
            );

            this._eventService.OnSupervisionListChangeMqtt.next(null);
          }
        }
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnLoginPlantDid",
          "REACT"
        );

        this.subscribeMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Get supervision by ID if the supervision is present inside store; otherwise, it return null.
   */
  public getSupervisionByID(idSupervision: number): Supervision {
    let returnSupervision: Supervision = null;

    if (this._supervisionStore?.data) {
      const indexSupervision = this._supervisionStore.data.findIndex(
        (sup) => sup.id === idSupervision
      );
      if (indexSupervision >= 0) {
        returnSupervision = this._supervisionStore.data[indexSupervision];
      }
    }

    return returnSupervision;
  }

  /**
   *
   * Retrive component and state address concurrently
   *
   * @param idSupervision idCustomInterface to retrive component and state addresses
   * @param idEnviroment idEnvironmente releated to custom itnerface
   * @param propagateComponentUpdate Enable propagation component update directly
   * @param propagateAddressesUpdate Enable propagation address update directly
   *
   */
  public async GetComponentAndStateAddressesByIDCustomInterfaface(
    idSupervision: number,
    idEnviroment: number,
    propagateComponentUpdate: boolean,
    propagateAddressesUpdate: boolean
  ) {
    this._lastDiplayedSupervsionID = idSupervision;
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "GetComponentAndStateAddressesByIDCustomInterfaface",
      "New _lastDiplayedSupervsionID: " + this._lastDiplayedSupervsionID
    );

    const indexSupervision = this._supervisionStore.data.findIndex(
      (sup) => sup.id === idSupervision && sup.idEnvironment === idEnviroment
    );

    if (indexSupervision >= 0) {
      try {
        const resultChaningPromise = await Promise.all([
          this.GetAdressesByIDCustomInterface(
            idSupervision,
            idEnviroment,
            propagateAddressesUpdate
          ),
          this.GetComponentsByIDSupervision(
            idSupervision,
            idEnviroment,
            propagateComponentUpdate
          ),
        ]);

        if (
          resultChaningPromise.length === 2 &&
          resultChaningPromise[0] != null &&
          resultChaningPromise[1] != null
        ) {
          let aggregateList: ComponentAggregation[] = [];

          let aggregateIds: Array<string> = new Array<string>();
          const addressesString: Array<string> = new Array<string>();

          let listSupervisionAddress: Address[] = resultChaningPromise[0];
          let listSupervisionComponent: SupervisionComponentAPI[] =
            resultChaningPromise[1];

          await this.loadSupervisionCommandAddresses(
            idSupervision,
            listSupervisionComponent
          );

          aggregateList = await this.composeSupervisionAggregateList(
            idSupervision,
            listSupervisionComponent
          );

          this._supervisionStore.data[indexSupervision].componentsAggregation =
            aggregateList;

          this._supervisionStore.data[indexSupervision].components =
            listSupervisionComponent;
          this._supervisionStore.data[indexSupervision].adresses =
            listSupervisionAddress;

          this._supervisionListSubject.next(this._supervisionStore.data);
          this.setToStorage(this._supervisionStore);

          return this._supervisionStore.data[indexSupervision];
        } else {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "GetComponentAndStateAddressesByIDCustomInterfaface() ",
            "ERROR: resultChaningPromise NOT 2"
          );
          return null;
        }
      } catch (exception) {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "GetComponentAndStateAddressesByIDCustomInterfaface() ",
          "ERROR catch: " + exception.message
        );
        return null;
      }
    } else {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "GetComponentAndStateAddressesByIDCustomInterfaface() ",
        "ERROR: Supervision NOT FOUND"
      );
      return null;
    }
  }

  /**
   * Update supervision tab order by specific id
   *
   * @param listAggregate Full list supervision aggregate
   * @param idSupervison id Supervision to apply custom reorder
   */
  public UpdateUserTabOrder(
    listAggregate: ComponentAggregation[],
    idSupervison: number
  ) {
    this.saveUserCustomTabOrder(
      listAggregate,
      idSupervison,
      this._currentPlant
    );
  }

  /**
   * Perform a GET on API/custominterface trying the new "/address" endpoint first and
   * falling back to the legacy "/stateaddress" path when the server does not expose it
   *
   * @param newPath new "/address" path
   * @param legacyPath legacy "/stateaddress" path used on older API servers
   * @returns the API response
   */
  private getCustomInterfaceWithLegacyFallback<T>(
    newPath: string,
    legacyPath: string
  ): Observable<T> {
    const baseUrl =
      environment.httpServerRoot + AppSettings.API_URL_CUSTOMINTERFACE;

    return this._http.get<T>(baseUrl + newPath).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404 || error.status === 0 || error.status === 405) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "getCustomInterfaceWithLegacyFallback()",
            "New endpoint " +
            newPath +
            " not available (status " +
            error.status +
            "), falling back to legacy " +
            legacyPath
          );

          return this._http.get<T>(baseUrl + legacyPath);
        }

        // Any other error is a real failure and must not be masked by the fallback.
        return throwError(() => error);
      })
    );
  }

  /**
   * Get single state address by instance guid and address
   *
   * @param instanceGUID InstanceGUID to find address
   * @param address Address to find
   * @returns
   */
  public async getSingleAddress(instanceGUID: string, address: string) {
    const queryString: string =
      "?address=" +
      encodeURIComponent(instanceGUID) +
      "_" +
      encodeURIComponent(address);

    return this.getCustomInterfaceWithLegacyFallback<Address>(
      "/address" + queryString,
      "/stateaddresses" + queryString
    )
      .pipe(
        tap((foundAddress: Address) => {
          if (foundAddress.id > 0) {
            this._addressService.addAddress(foundAddress);
          }
        })
      )
      .toPromise();
  }

  //#region State Addresses API Operation
  /**
   * Get specific stuom interface state addresses
   *
   * @param idSupervision id Custom interface
   * @param propagateAddressesUpdate Propagate component to custom interface update:
   * if is set to true propataion in made inside the function
   * otherwise it should be done manually outside the function (OPTIONAL) //
   */
  async GetAdressesByIDCustomInterface(
    idSupervision: number,
    idEnviroment: number,
    propagateAddressesUpdate?: boolean
  ) {
    propagateAddressesUpdate = !!propagateAddressesUpdate;

    return this._http
      .get<Array<Address>>(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMINTERFACE +
        "/" +
        idSupervision +
        "/" +
        idEnviroment +
        "/stateaddresses"
      )
      .pipe(
        catchError((error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            " ._http.get -" +
            AppSettings.API_URL_CUSTOMINTERFACE +
            "/" +
            idSupervision +
            "/" +
            idEnviroment +
            "/stateaddresses",
            " GetAdressesByIDCustomInterface() ",
            "ERROR: " + error.message
          );

          return of(null);
        }),
        tap((addresses: Array<Address>) => {
          if (addresses) {
            const addressesArrayString: Array<string> = new Array<string>();

            const indexSupervision = this._supervisionStore.data.findIndex(
              (sup) => sup.id === idSupervision
            );

            this._supervisionStore.data[indexSupervision].adresses = addresses;
          }
        }),
        tap(async (addresses: Array<Address>) => {
          if (addresses && propagateAddressesUpdate) {
            await this._addressService.addAddresses(addresses);
          }
        })
      )
      .toPromise();
  }

  //#endregion

  //#region Supervision Component API Operation
  /**
   *
   * @param idSupervision id Custom interface
   * @param idEnviroment id Releated environment to custom interface (OPTIONAL)
   * @param propagateComponentUpdate Propagate component to custom interface update:
   * if is set to true propataion in made inside the function
   * otherwise it should be done manually outside the function (OPTIONAL)
   */
  public async GetComponentsByIDSupervision(
    idSupervision: number,
    idEnviroment?: number,
    propagateComponentUpdate: boolean = true
  ) {
    let idEnv = 0;

    if (!!idEnviroment) {
      idEnv = idEnviroment;
    }

    return this._http
      .get(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMINTERFACE +
        "/" +
        idSupervision +
        "/" +
        idEnv +
        "/components"
      )
      .pipe(
        catchError((error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            " ._http.get -" +
            AppSettings.API_URL_CUSTOMINTERFACE +
            "/" +
            idSupervision +
            "/" +
            idEnv +
            "/components",
            " GetComponentsByIDSupervision() ",
            "ERROR: " + error.message
          );
          return of(null);
        }),
        tap((componentList: Array<SupervisionComponentAPI>) => {
          if (propagateComponentUpdate) {
            const indexSupervision = this._supervisionStore.data.findIndex(
              (supervision: Supervision) => supervision.id === idSupervision
            );

            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_http.get - " + AppSettings.API_URL_CUSTOMINTERFACE,
              "GetComponentsByIDSupervision()",
              "IS componentList null: " + (componentList === null)
            );

            this._supervisionStore.data[indexSupervision].components = [
              ...componentList,
            ];

            this._supervisionListSubject.next(this._supervisionStore.data);
            this.setToStorage(this._supervisionStore);
          }
        })
      )
      .toPromise();
  }

  //#endregion

  public setEnableReturnHome(idSupervision, enableReturnHome) {
    const bodyRequest = {
      Active: enableReturnHome,
    };
    return this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMINTERFACE +
        "/" +
        idSupervision +
        "/enableReturnHome/",
        bodyRequest
      )
      .pipe(
        tap(() => {
          const indexSupervision = this._supervisionStore.data.findIndex(
            (supervision: Supervision) => supervision.id === idSupervision
          );

          this._supervisionStore.data[indexSupervision].enableReturnHome =
            enableReturnHome;

          this._supervisionListSubject.next(this._supervisionStore.data);
          this.setToStorage(this._supervisionStore);
        })
      )
      .toPromise();
  }

  /**
   * This method defines the actual landing page, applying all the needed conditions and considerations to the putative page the
   * user should be eligible to by server-side configuration.
   * @returns
   */
  public defineFirstPageToOpen(): number {
    let firstPageActual: number =
      Number(this._userSettings.DefaultHomepageUser) ||
      PageSection.SUPERVISION_CUSTOM_INTERFACE_LIST;

    if (
      this._currentPlant?.operativeMode ===
      BIGDeviceOperationMode.ACCESS_CONTROL_MODE
    ) {
      if (
        firstPageActual !== PageSection.SUPERVISION_CUSTOM_INTERFACE &&
        firstPageActual !== PageSection.SUPERVISION_CUSTOM_INTERFACE_LIST
      ) {
        firstPageActual = PageSection.SUPERVISION_CUSTOM_INTERFACE_LIST;
      }
    }

    return firstPageActual;
  }

  /**
   * Set tab to open
   */
  public async setDefaultAccessTabToOpenByFirstPage(): Promise<void> {
    let landingAccessTab: number = EnvironmentCategory.COMMON_AREA;

    // Da gestire solo page section legate agli accessi //
    switch (Number(this._userSettings.DefaultHomepageUser)) {
      case PageSection.ACCESSES_COMMON:
        landingAccessTab = EnvironmentCategory.COMMON_AREA;
        break;
      case PageSection.ACCESSES_ROOM:
        landingAccessTab = EnvironmentCategory.ROOM;
        break;
      case PageSection.ACCESSES_SUITE:
        landingAccessTab = EnvironmentCategory.SUITE;
        break;
      case PageSection.ACCESSES_JUNIOR_SUITE:
        landingAccessTab = EnvironmentCategory.JUNIOR_SUITE;
        break;
      case PageSection.ACCESSES_SINGLE_ROOM:
        landingAccessTab = EnvironmentCategory.SINGLE_ROOM;
        break;
      case PageSection.ACCESSES_DOUBLE_ROOM:
        landingAccessTab = EnvironmentCategory.DOUBLE_ROOM;
        break;
      case PageSection.ACCESSES_TRIPLE_ROOM:
        landingAccessTab = EnvironmentCategory.TRIPLE_ROOM;
        break;
      case PageSection.ACCESSES_FAMILY_ROOM:
        landingAccessTab = EnvironmentCategory.FAMILY_ROOM;
        break;
      case PageSection.ACCESSES_DORMITORY:
        landingAccessTab = EnvironmentCategory.DORMITORY;
        break;
      case PageSection.ACCESSES_ELEVATOR_FLOOR:
        landingAccessTab = EnvironmentCategory.ELAVATOR_FLOOR;
        break;
      case PageSection.ACCESSES_CABINETS:
        landingAccessTab = EnvironmentCategory.CABINETS;
        break;
      case PageSection.ACCESSES_SKI_DEPOSITIS:
        landingAccessTab = EnvironmentCategory.SKI_DEPOSITS;
        break;
      case PageSection.ACCESSES_EXTERNAL_ACCESS:
        landingAccessTab = EnvironmentCategory.EXTERNAL_ACCESS;
        break;
      default:
        landingAccessTab = EnvironmentCategory.COMMON_AREA;
        break;
    }

    this._store.dispatch(
      clientSettingsUpdateCustomStartingAccessEnvironmentcartegoryAction({
        accessCategory: landingAccessTab,
      })
    );
  }

  /**
   * Restore current supervision element tab order deleting storage key reference and propagating specific event
   *
   * @param idSupervision ID supervision to restore order
   * @param idEnvironment ID environrment to restor supervision order
   */
  public async restoreSupervisionTabOrderToDefault(
    idSupervision: number,
    idEnvironment: number = 0
  ): Promise<void> {
    try {
      const idCurrentPlant: number = this._currentPlant?.ID || -1;

      const storageCustomeInterfaceAggrOrder: CustomInterfaceAggregateOrder[] =
        await this._storageService.get(
          AppSettings.KEY_CUSTOMINTERFACEAGGREGATEORDER_ + idCurrentPlant
        );

      const indexAggrOrder = storageCustomeInterfaceAggrOrder.findIndex(
        (obj: CustomInterfaceAggregateOrder) => {
          return obj.customInterfaceID === idSupervision;
        }
      );

      if (indexAggrOrder >= 0) {
        storageCustomeInterfaceAggrOrder.splice(indexAggrOrder, 1);

        this._storageService.set(
          AppSettings.KEY_CUSTOMINTERFACEAGGREGATEORDER_ + idCurrentPlant,
          storageCustomeInterfaceAggrOrder
        );

        this._eventService.OnSupervisionRestoreDefualtOrder.next(idSupervision);
      }
    } catch (exception) { }
  }

  /**
   * Handle fallback property for "Visible" component property.
   * If property not exist in original object, isVisible parameter is set to undefined.
   * It this case isVisible for default is set to true.
   *
   * @param isVisible is visible
   * @returns
   */
  public isComponentVisible(isVisible: boolean) {
    let visible: boolean = true;

    if (isVisible !== undefined) {
      visible = isVisible;
    }

    return visible;
  }

  //#region Mqtt addresses handling

  /**
   * GF: update addresses on ci are used only to send to the pages the information that a custom interface has been changed
   * or deleted. So the pages can show an alert message (if necessary) and the user can chose to reload the informations
   */
  async subscribeMQTTAddresses() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "subscribeMQTTAddresses",
      "START"
    );

    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_CUSTOM_INTERFACE_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_CUSTOM_INTERFACE_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    //this._mqttAddressSubscriptions.push(
    mqttAddressSubcription = returnedObservable
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        })
      )
      .subscribe(
        async (idCustomInterfaceChanged) => {
          let resInitialisation: boolean = false;
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqttService.subscribeAddress",
            "AppReserved.RESERVED_CUSTOM_INTERFACE_CHANGED",
            "ID CustomInterfaceChanged: " + idCustomInterfaceChanged
          );

          resInitialisation = await this.initializeSupervisionList(
            idCustomInterfaceChanged
          );

          if (resInitialisation) {
            this._eventService.OnCustomInterfaceChanged.next(
              idCustomInterfaceChanged
            );
          }
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeTopic",
            "AppReserved.RESERVED_CUSTOM_INTERFACE_CHANGED",
            "Error: " + error
          );
        }
      );

    let res = await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );
    if (res) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeMQTTAddresses",
        mqttAddress + " SETTED"
      );
    }

    //#endregion

    //#region Subscribing to RESERVED_CUSTOM_INTERFACE_DELETED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_CUSTOM_INTERFACE_DELETED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        })
      )
      .subscribe(
        async (idCustomInterfaceDeleted) => {
          let resInitialisation: boolean = false;
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqttService.subscribeAddress",
            "AppReserved.RESERVED_CUSTOM_INTERFACE_DELETED",
            "ID CustomInterfaceDeleted: " + idCustomInterfaceDeleted
          );

          resInitialisation = await this.initializeSupervisionList(
            idCustomInterfaceDeleted
          );

          if (resInitialisation) {
            this._eventService.OnCustomInterfaceDeleted.next(
              idCustomInterfaceDeleted
            );
          }
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeTopic",
            "AppReserved.RESERVED_CUSTOM_INTERFACE_DELETED",
            "Error: " + error
          );
        }
      );

    let res1 = await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );
    if (res1) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeMQTTAddresses",
        mqttAddress + " SETTED"
      );
    }

    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "unsubscribeToMQTTAddresses()",
      "LOCAL MAP-lenght : " +
      Array.from(this._mqttAddressSubscriptionsMap.entries()).length
    );

    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      if (entry[1] != null) {
        entry[1].unsubscribe();
      }

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "unsubscribeToMQTTAddresses()",
        "ENTRY TO UNSUB. : " + entry[0] + " -- SUBSCRIPTION?: " + entry[1]
      );

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  //#region Supervision API Operation
  /**
   * Get Supervisions API function. If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetSupervisionListAPIRequest" to
   * TRUE, it set the store to NULL and emit on the osbservable a neutral value (IE. empty array).
   */
  private async GetSupervisions() {
    if (!this._isGettingSupervsionListOnGoing) {
      this._isGettingSupervsionListOnGoing = true;
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "GetSupervisions",
        "IN GetSupervisions"
      );

      let queryString: string = "";

      if (
        this._currentBuilding
      ) {
        queryString += "?idArea=" + this._currentBuilding.idAreaLocal;
      }

      if (
        !queryString
      ) {
        this.resetOnErrorOccurred();
      } else {
        return this._http
          .get<GenericApiData>(
            environment.httpServerRoot +
            AppSettings.API_URL_CUSTOMINTERFACE +
            queryString
          )
          .toPromise()
          .then(
            (returnSupervisionStore: GenericApiData) => {
              this._loggerService.log(
                LogLevel.LOG_INFO,
                this._logContext,
                "_http.get /api/custominterface",
                "GetSupervisions",
                " DATA RETURNED"
              );

              this._isErrorInGetSupervisionListAPIRequest = false;
              this._supervisionStore = returnSupervisionStore;

              this._supervisionListSubject.next(returnSupervisionStore.data);
              this.setToStorage(this._supervisionStore);

              this._isGettingSupervsionListOnGoing = false;

              this._loggerService.log(
                LogLevel.LOG_INFO,
                this._logContext,
                "_http.get /api/custominterface",
                "GetSupervisions",
                " END SUCCESS"
              );
            },
            (error) => {
              this._loggerService.log(
                LogLevel.LOG_ERROR,
                this._logContext,
                "_http.get /api/custominterface",
                "GetSupervisions",
                "ERROR! : " + error.message
              );

              this.resetOnErrorOccurred();
            }
          );
      }
    }
  }

  /**
   * Get Supervisions API function by last modify.
   *If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetSupervisionListAPIRequest" to
   * TRUE, it leaves the store as it is and emit on the osbservable a neutral value (IE. empty array). //
   * @param supervisionStore
   * @returns
   */
  private GetSupervisionsByLastModify(supervisionStore: GenericApiData) {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "GetSupervisionsByLastModify",
      "IN GetSupervisionsByLastModify"
    );
    if (!this._isGettingSupervsionListOnGoing) {
      this._isGettingSupervsionListOnGoing = true;

      let queryString: string =
        "?lastModified=" + encodeURIComponent(supervisionStore.lastAPIModified);

      return this._http
        .get<GenericApiData>(
          environment.httpServerRoot +
          AppSettings.API_URL_CUSTOMINTERFACE +
          queryString
        )
        .toPromise()
        .then(
          (returnSupervisionStore: GenericApiData) => {
            this._isErrorInGetSupervisionListAPIRequest = false;

            if (returnSupervisionStore) {
              supervisionStore = returnSupervisionStore;

              this.setToStorage(supervisionStore);
            }
            this._supervisionStore = supervisionStore;
            this._supervisionListSubject.next(supervisionStore.data);

            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_http.get /api/custominterface",
              "GetSupervisionsByLastModify",
              "EMITTED"
            );
            this._isGettingSupervsionListOnGoing = false;
          },
          (error) => {
            this._isErrorInGetSupervisionListAPIRequest = true;
            this._isGettingSupervsionListOnGoing = false;

            this._loggerService.log(
              LogLevel.LOG_ERROR,
              this._logContext,
              "_http.get /api/custominterface",
              "GetSupervisionsByLastModify",
              "Error " + error.message
            );
            this._supervisionListSubject.next([]);
          }
        );
    }
  }
  //#endregion

  //

  /**
   * Initialize supervion list
   */
  private async initializeSupervisionList(
    idSupervsionChangedArray: string[] = null
  ): Promise<boolean> {
    let continueInitilialize: boolean = true;

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "initializeSupervisionList",
      "START"
    );

    const resultReadStorage: GenericApiData = await this.getFromStorage();

    if (
      idSupervsionChangedArray !== null &&
      idSupervsionChangedArray !== undefined
    ) {
      const localSupervisionList: Array<Supervision> =
        resultReadStorage?.data || new Array<Supervision>();
      let found: boolean = false;

      for (let j = 0; j < idSupervsionChangedArray.length && !found; j++) {
        for (let i = 0; i < localSupervisionList.length && !found; i++) {
          found =
            localSupervisionList[i].id == parseInt(idSupervsionChangedArray[j]);
        }
      }

      continueInitilialize = found;
    }

    if (continueInitilialize) {
      await this.GetSupervisions();
    }

    return continueInitilialize;
  }

  //#region Storage
  protected setToStorage(superivisonStore: GenericApiData) {
    this._storageService.set(AppSettings.KEY_SUPERVISIONS, superivisonStore);
  }

  protected getFromStorage(): Promise<GenericApiData> {
    // from the local storage inside the local array
    return this._storageService.get(AppSettings.KEY_SUPERVISIONS).then(
      (superivsionStore: GenericApiData) => {
        if (superivsionStore) {
          return superivsionStore;
        } else {
          return null;
        }
      },
      (error) => {
        return null;
      }
    );
  }

  //#endregion

  /**
   * Reload service data (es. in case of error)
   */
  public async reloadServiceData() {
    this.needToInitialize = true;

    await this.Init();
  }

  /**
   * Compose full list of component aggreagation by i Supervision and list of component aggregation
   *
   * @param idSupervision ID supervision to get key of custom aggreagiont
   * @param listSupervisionComponent List of component to filter aggreagation
   * @returns
   */
  private async composeSupervisionAggregateList(
    idSupervision: number,
    listSupervisionComponent: Array<SupervisionComponentAPI>
  ): Promise<Array<ComponentAggregation>> {
    let listAggregate: Array<ComponentAggregation> =
      new Array<ComponentAggregation>();
    let listAggreageIds: Array<string> = new Array<string>();

    try {
      const idCurrentPlant: number = this._currentPlant?.ID || -1;

      for (const component of listSupervisionComponent) {
        if (
          listAggreageIds.indexOf(component.properties["AggregateID"]) === -1 &&
          component.properties["AggregateID"] !== "" &&
          component.properties["AggregateID"]
        ) {
          listAggreageIds.push(component.properties["AggregateID"]);
        }
      }

      if (listAggreageIds.length > 0) {
        listAggregate = this.handleComponentAggregationByAggregateID(
          listAggreageIds,
          listSupervisionComponent
        );
      }

      listAggregate = listAggregate
        .filter((aggregate) => aggregate.idAggregate !== "")
        .filter((aggregate) => aggregate.listComponent.length > 0);

      listAggregate = await this.loadUserTabAggregationFromStorage(
        listAggregate,
        idCurrentPlant,
        idSupervision
      );

      listAggregate = listAggregate.sort((a, b) => {
        return a.tabOrder - b.tabOrder;
      });
    } catch (exception) {
      listAggregate = [];

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "composeSupervisionAggregateList",
        "IN composeSupervisionAggregateList"
      );
    }

    return listAggregate;
  } //

  /**
   * Handle component aggregation with aggregation type by id
   *
   * @param aggregateIds Aggregation Ids array
   * @param supervisionComponents Supervision component
   * @returns
   */
  private handleComponentAggregationByAggregateID(
    aggregateIds: Array<string>,
    listSupervisionComponent: Array<SupervisionComponentAPI>
  ): Array<ComponentAggregation> {
    const listAggregate = new Array<ComponentAggregation>();

    for (const idAggregate of aggregateIds) {
      const aggregate: ComponentAggregation = new ComponentAggregation();
      const componentSupervision: Array<SupervisionComponentAPI> =
        new Array<SupervisionComponentAPI>();

      for (const component of listSupervisionComponent) {
        if (
          component.properties["AggregateID"] &&
          component.properties["AggregateID"] === idAggregate &&
          component.mobileVisualization &&
          this.isComponentVisible(component.visible)
        ) {
          componentSupervision.push(component);
          aggregate.instanceGUID =
            component.properties["InstanceGUIDState"] || "";
        }
      }

      // then create the final object
      aggregate.idAggregate = idAggregate;
      aggregate.address =
        this._addressService.getAddressByInstanceGUIDAndAddress(
          aggregate.instanceGUID,
          idAggregate
        );
      aggregate.listComponent = [...componentSupervision];

      aggregate.listComponent.sort(
        (a, b) =>
          parseInt(a.properties["Left"].replace("px", ""), 10) -
          parseInt(b.properties["Left"].replace("px", ""), 10)
      );

      aggregate.refreshAggregateTabOrder();
      aggregate.calculateAggregationName();

      listAggregate.push(aggregate);
    }

    return listAggregate;
  }

  /**
   * Save user custom tab order set after drag/drop action on list supervision page
   *
   * @param aggregateList Supervision aggregate element
   * @param idSupervision ID supervision loade
   * @param currentPlant Current referenced plant
   */
  private async saveUserCustomTabOrder(
    aggregateList: Array<ComponentAggregation>,
    idSupervision: number,
    currentPlant: Plant
  ): Promise<void> {
    try {
      let listCustomIntAggregateOrder: Array<CustomInterfaceAggregateOrder> =
        (await this._storageService.get(
          AppSettings.KEY_CUSTOMINTERFACEAGGREGATEORDER_ + currentPlant.ID
        )) || [];

      let indexAggregate = listCustomIntAggregateOrder.findIndex(
        (obj: CustomInterfaceAggregateOrder) => {
          return obj.customInterfaceID === idSupervision;
        }
      );

      /** If custom aggregator order component not exist in storage it create in runtime */
      if (indexAggregate === -1) {
        const newCustomeInterfaceAggregator =
          new CustomInterfaceAggregateOrder();
        newCustomeInterfaceAggregator.customInterfaceID = idSupervision;
        indexAggregate = 0;

        newCustomeInterfaceAggregator.aggregatesOrder = aggregateList.map(
          (aggregate) => {
            return new AggregateOrder(
              aggregate.idAggregate,
              aggregate.tabOrder
            );
          }
        );

        listCustomIntAggregateOrder.push(newCustomeInterfaceAggregator);
      }

      if (indexAggregate >= 0) {
        for (const aggregate of aggregateList) {
          const singleIdx = listCustomIntAggregateOrder[
            indexAggregate
          ].aggregatesOrder.findIndex((agg: AggregateOrder) => {
            return agg.aggregateID === aggregate.idAggregate;
          });
          if (singleIdx >= 0) {
            listCustomIntAggregateOrder[indexAggregate].aggregatesOrder[
              singleIdx
            ].aggregateUserTabOrder = aggregate.tabOrder;
          }
        }
      }

      this._storageService.set(
        AppSettings.KEY_CUSTOMINTERFACEAGGREGATEORDER_ + currentPlant.ID,
        listCustomIntAggregateOrder
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "saveUserCustomTabOrder",
        "Error: " + exception.message
      );
    }
  }

  /**
   * Load custom aggregation order from storage to read user order preference
   *
   * @param listAggregateComponents Full aggregation list
   * @param idPlant idPlant to get aggregation preference key
   * @param idSupervision id Specific supervision to get preferences
   */
  private async loadUserTabAggregationFromStorage(
    listAggregateComponents: Array<ComponentAggregation>,
    idPlant: number,
    idSupervision: number
  ) {
    try {
      const storageCustomeInterfaceAggrOrder: CustomInterfaceAggregateOrder[] =
        await this._storageService.get(
          AppSettings.KEY_CUSTOMINTERFACEAGGREGATEORDER_ + idPlant
        );

      if (storageCustomeInterfaceAggrOrder) {
        const indexAggrOrder = storageCustomeInterfaceAggrOrder.findIndex(
          (obj: CustomInterfaceAggregateOrder) => {
            return obj.customInterfaceID === idSupervision;
          }
        );

        if (indexAggrOrder >= 0) {
          for (
            let indexOrig = 0;
            indexOrig < listAggregateComponents.length;
            indexOrig++
          ) {
            const singleAggregateIndex = storageCustomeInterfaceAggrOrder[
              indexAggrOrder
            ].aggregatesOrder.findIndex((agg: AggregateOrder) => {
              return (
                agg.aggregateID.toUpperCase() ===
                listAggregateComponents[indexOrig].idAggregate.toUpperCase()
              );
            });

            if (singleAggregateIndex >= 0) {
              listAggregateComponents[indexOrig].updateAggregateTabOrder(
                storageCustomeInterfaceAggrOrder[indexAggrOrder]
                  .aggregatesOrder[singleAggregateIndex].aggregateUserTabOrder
              );
            } else {
              storageCustomeInterfaceAggrOrder[
                indexAggrOrder
              ].aggregatesOrder.push(
                new AggregateOrder(
                  listAggregateComponents[indexOrig].idAggregate,
                  listAggregateComponents[indexOrig].tabOrder
                )
              );
            }
          }

          this._storageService.set(
            AppSettings.KEY_CUSTOMINTERFACEAGGREGATEORDER_ + idPlant,
            storageCustomeInterfaceAggrOrder
          );
        }
      }
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "loadUserTabAggregationFromStorage",
        "Exception: " + exception
      );
    }

    return listAggregateComponents;
  }

  /**
   * Load supervision not yet registered address (like command addresses) and
   * register it in address service.
   *
   * In the peculiar case of "Command Addresses", it also LOCALLY checks if they have already been registered; if it is so, it DOSE NOT ask "Address.service"
   * to update the address saved inside its map, as this address type's "lastValue" property is not meaningful.
   *
   * In other words, the "command" address are requested from server and subscribed to ONLY once, while "stateAddresses" are requested/updated each data refresh.
   *
   * @param idSupervision
   * @param listSupervisionComponent
   */
  private async loadSupervisionCommandAddresses(
    idSupervision: number,
    listSupervisionComponent: Array<SupervisionComponentAPI>
  ): Promise<void> {
    const promisesCommandAddress: Array<Promise<any>> = new Array<
      Promise<any>
    >();
    let listSupervisionCommandAddress: Array<Address> = null;
    let addressToSubscribeString: Array<string> = new Array<string>();

    try {
      for (const component of listSupervisionComponent) {
        const instanceGUID = component.properties["InstanceGUIDCommand"] || "";
        const address = component.properties["AddressCommand"] || "";

        if (
          address &&
          instanceGUID &&
          !this._addressService.isAddressSubscribed(instanceGUID, address) &&
          addressToSubscribeString.indexOf(instanceGUID + "_" + address) === -1
        ) {
          addressToSubscribeString.push(instanceGUID + "_" + address);

          promisesCommandAddress.push(
            this.getSingleAddress(
              component.properties["InstanceGUIDCommand"],
              component.properties["AddressCommand"]
            )
          );
        }
      }

      listSupervisionCommandAddress = await Promise.all(promisesCommandAddress);
      listSupervisionCommandAddress = listSupervisionCommandAddress.filter(
        (address: Address) => address.id > 0
      );

      await this._addressService.addAddresses(listSupervisionCommandAddress);
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "loadSupervisionCommandAddresses()",
        "ERROR: " + exception.message
      );
    }
  }

  /**
   * Subscribe to plant service variable
   */
  private subscribePlant(): void {
    this._selectSelectedPlantUserSub = this._store
      .select(selectedPlant)
      .subscribe((currentPlant: Plant) => {
        this._currentPlant = currentPlant;
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectSelectedPlantUserSub) {
      this._selectSelectedPlantUserSub.unsubscribe();
    }
  }
  //#endregion

  /**
   * This resets the loading of supervisions in the case something goes wrong
   */
  private resetOnErrorOccurred(): void {
    this._isGettingSupervsionListOnGoing = false;
    this._isErrorInGetSupervisionListAPIRequest = true;
    this._supervisionStore = null;

    this._supervisionListSubject.next([]);
  }
}
